import Bugsnag from '@bugsnag/js';
import type { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import React from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider } from 'styled-components';

import App from './App';
import { isDomainMismatchError } from './constants/errors/domainMismatch';
import { handleErrors } from './contexts/helpers';
import DomainAccessError from './pages/DomainAccessError/DomainAccessError';
import MaintenancePage from './pages/Maintenance/Maintenance';
import { getSessionData } from './services/session';

type ErrorFallbackComponent = NonNullable<
  React.ComponentProps<BugsnagErrorBoundary>['FallbackComponent']
>;

interface BootstrapOptions {
  ErrorBoundary: BugsnagErrorBoundary;
  ErrorView: ErrorFallbackComponent;
  isUnderMaintenance: boolean;
  theme: React.ComponentProps<typeof ThemeProvider>['theme'];
}

export { DOMAIN_MISMATCH_ERROR, isDomainMismatchError } from './constants/errors/domainMismatch';

const reportBootstrapError = (error: unknown): void => {
  try {
    // `handleErrors` performs the established authentication redirects and reports other
    // API failures. It intentionally throws after handling them, so bootstrap must absorb
    // that throw in order to render a recovery view.
    handleErrors(error as Parameters<typeof handleErrors>[0]);
  } catch (_handledError) {
    // Expected: `handleErrors` signals handled failures by throwing.
  }
};

export const bootstrapApp = async ({
  ErrorBoundary,
  ErrorView,
  isUnderMaintenance,
  theme,
}: BootstrapOptions): Promise<void> => {
  const render = (content: JSX.Element) => {
    ReactDOM.render(
      <ErrorBoundary FallbackComponent={ErrorView}>
        <ThemeProvider theme={theme}>{content}</ThemeProvider>
      </ErrorBoundary>,
      document.getElementById('root')
    );
  };

  try {
    const {
      data: { user },
    } = await getSessionData();

    const profileData = user?.uaa_data.profile.data;

    Bugsnag.addOnError((event) => {
      event.setUser(user.uaa_id, user.email, `${profileData.first_name} ${profileData.last_name}`);
    });

    render(isUnderMaintenance ? <MaintenancePage /> : <App />);
  } catch (error) {
    const isDomainMismatch = isDomainMismatchError(error);
    reportBootstrapError(error);
    render(isDomainMismatch ? <DomainAccessError /> : <MaintenancePage />);
  }
};
